import { FC, useRef, useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { Formik } from 'formik';
import {
  Box,
  Button,
  CircularProgress,
  // FormHelperText,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  // Link,
  Radio,
  TextField,
  Typography,
} from '@material-ui/core';
import useAuth from 'src/hooks/useAuth';
import useMounted from 'src/hooks/useMounted';
import { Visibility, VisibilityOff } from '@material-ui/icons';
import PhoneInput from 'react-phone-input-2';
import 'src/theme/phone_element.css';
import ReCAPTCHA from 'react-google-recaptcha';
import axios from 'axios';
import { accsiomURIs } from 'src/config';
import { httpCheckClientOrgStatus, httpCheckMemberStatus, httpGetInvitationDetails, httpRegisterUserOnInvite } from 'src/apis/httpDispatch';
import { getClientAddress } from 'src/utils/getAddressUtils';
import cleanUp from 'src/utils/cleanUp';
import { LoadingStatus, setLoadingStatus } from 'src/slices/user';
import { useDispatch } from 'src/store';
import PopupTooltip from 'src/components/dialog/PopupTooltip';
import DblButtonMessage from 'src/components/dialog/DblButtonMessage';

const RegisterAmplify: FC = (props) => {
  const mounted = useMounted();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { register, login } = useAuth() as any;
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const captchaRef = useRef<ReCAPTCHA>();
  const { code } = useParams();
  const [registerInfo, setRegisterInfo] = useState<any>(null);
  const [isLoading, setLoading] = useState<boolean>(true);
  const [phonePrefix, setPhonePrefix] = useState<string>('');
  const [isToEmail, setToEmail] = useState<boolean>(true);
  const [isDuplicateUser, setIsDuplicateUser] = useState<boolean>(false);
  const emailIdentified = registerInfo && !registerInfo.asClientOrg;

  const getRegisterInfo = useCallback(async () => {
    if (!mounted.current) return;
    setLoading(true);
    const addrInfo = await getClientAddress();
    if (!mounted.current) return;
    if (addrInfo) setPhonePrefix(addrInfo.phone);
    if (code) {
      try {
        const { params, declined, inviteModel, accepted } = await httpGetInvitationDetails(encodeURIComponent(code));
        if (!mounted.current) return;
        if (new Date(params.expire) < new Date()) {
          setRegisterInfo({
            error: 'Url for Sign up has already expired. This link cannot be used to sign up anymore.'
          });
        } else if (declined) {
          setRegisterInfo({
            error: 'This invitation is declined. This link cannot be used to sign up anymore.'
          });
        } else if (accepted) {
          setRegisterInfo({
            error: 'This invitation is already accepted.'
          });
        } else if (!params.asClientOrg) {
          const { needs } = await httpCheckMemberStatus(params.target, params.organizationId);
          if (needs === 'error') {
            if (mounted.current) {
              setRegisterInfo({
                error: 'You are already registered in this organization.'
              });
            }
          } else if (needs === 'login') {
            navigate('/login', { state: { params, isUserAccept: true } });
          } else if (needs === 'register') {
            if (mounted.current) {
              setRegisterInfo({
                ...params,
                inviteModel
              });
            }
          }
        } else {
          const res = await httpCheckClientOrgStatus(params.target);
          if (res.needs === 'login') {
            navigate('/login', { state: { params: { ...params, inviteModel }, isUserAccept: false, org: res.orgInfo } });
          } else if (res.needs === 'register') {
            if (mounted.current) {
              setRegisterInfo({
                ...params,
                inviteModel
              });
            }
          }
        }
      } catch (err) {
        console.log(JSON.stringify(err));
        if (mounted.current) {
          setRegisterInfo({
            error: 'Failed to load data. Please try again or check your sign up link.'
          });
        }
      }
    }
    if (mounted.current) setLoading(false);
  }, [code]);

  const onGotoLogin = () => {
    navigate('/login', { state: { params: registerInfo, isUserAccept: false } });
  };

  useEffect(() => {
    getRegisterInfo();
  }, [getRegisterInfo]);

  if (isLoading) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        height="400px"
      >
        <CircularProgress size={30} />
      </Box>
    );
  }

  if (registerInfo && registerInfo.error) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        height="400px"
      >
        <Typography
          color="secondary"
        >
          {registerInfo.error}
        </Typography>
      </Box>
    );
  }
  let firstN = '';
  let midN = '';
  let secondN = '';
  if (registerInfo?.inviteModel) {
    console.log(registerInfo);
    const split = registerInfo?.inviteModel?.penName?.split(' ');
    if (split) {
      if (split.length > 2) {
        // eslint-disable-next-line prefer-destructuring
        firstN = split[0];
        // eslint-disable-next-line prefer-destructuring
        midN = split[1];
        // eslint-disable-next-line prefer-destructuring
        secondN = split[2];
      } else if (split.length > 1) {
        // eslint-disable-next-line prefer-destructuring
        firstN = split[0];
        // eslint-disable-next-line prefer-destructuring
        secondN = split[1];
      } else {
        // eslint-disable-next-line prefer-destructuring
        firstN = split[0];
      }
    }
  }

  return (
    <>
      {
      (isDuplicateUser) && (
        <DblButtonMessage
          messageContent="Would you mind reset the password?"
          messageTitle="You already created this account before."
          onClose={() => {
          }}
          onYesClick={async () => {
            navigate('/authentication/password-recovery', { state: {
              state: {
                email: registerInfo.target.toLowerCase()
              }
            } });
          }}
        />
      )
    }
      <Formik
        initialValues={{
          email: (registerInfo && !registerInfo.asClientOrg) ? registerInfo.target.toLowerCase() : '',
          password: '',
          fname: (registerInfo && registerInfo.inviteModel.firstName) ? registerInfo.inviteModel.firstName : firstN,
          mname: (registerInfo && registerInfo.inviteModel.middleName) ? registerInfo.inviteModel.middleName : midN,
          lname: (registerInfo && registerInfo.inviteModel.lastName) ? registerInfo.inviteModel.lastName : secondN,
          submit: null,
          phoneNumber: (registerInfo && registerInfo.inviteModel.phone) ? registerInfo.inviteModel.phone : phonePrefix,
        }}
        validationSchema={
          Yup
            .object()
            .shape({
              email: Yup
                .string()
                .email('Must be a valid email')
                .max(255)
                .required('Email is required'),
              password: Yup
                .string()
                .min(7)
                .max(255)
                .required('Password is required'),
              fname: Yup
                .string()
                .required('First name is required'),
              lname: Yup
                .string()
                .required('Last name is required')
            })
        }
        onSubmit={async (values, {
          setErrors,
          setStatus,
          setSubmitting
        }): Promise<void> => {
          try {
            const captchaToken = await captchaRef.current.executeAsync();
            captchaRef.current.reset();
            const checkCaptcha = await axios.post(accsiomURIs.validate_human_uri, { token: captchaToken });
            if (checkCaptcha.status !== 201) return;
            if (!registerInfo) {
              await register(
                values.email.toLowerCase(),
                values.password,
                values.fname,
                values.mname,
                values.lname,
                `+${values.phoneNumber}`,
                registerInfo ? registerInfo.inviteModel.mid : '',
                isToEmail
              );
              navigate('/authentication/verify-code', {
                state: {
                  username: values.email.toLowerCase()
                }
              });
            } else {
              await httpRegisterUserOnInvite(
                encodeURIComponent(code),
                values.fname,
                values.mname,
                values.lname,
                values.phoneNumber,
                values.password
              );
              cleanUp();
              dispatch(setLoadingStatus(LoadingStatus.NONE));
              await login(values.email.toLowerCase(), values.password);
            }
          } catch (err) {
            if (err.message === 'Request failed with status code 400') {
              setIsDuplicateUser(true);
            }
            if (mounted.current) {
              setStatus({ success: false });
              setErrors({ submit: err.message });
              setSubmitting(false);
            }
          }
        }}
      >
        {({
          errors,
          handleBlur,
          handleChange,
          handleSubmit,
          isSubmitting,
          touched,
          values
        }): JSX.Element => (
          <form
            noValidate
            onSubmit={handleSubmit}
            {...props}
          >
            {registerInfo && (
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center'
                }}
              >
                <Typography
                  color="textSecondary"
                  variant="subtitle1"
                >
                  {!registerInfo.asClientOrg && registerInfo.asMember && `You are invited as "${registerInfo.roleName}" to ${registerInfo.orgName}.`}
                  {!registerInfo.asClientOrg && !registerInfo.asMember && `You are invited as client to ${registerInfo.orgName}.`}
                  {registerInfo.asClientOrg && `Sign up and establish a new organization as client to ${registerInfo.orgName}.`}
                </Typography>
              </Box>
            )}
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center'
              }}
            >
              <Typography
                color="textSecondary"
                variant="subtitle1"
              >
                I want to register as:
              </Typography>
            </Box>
            <Grid
              container
              spacing={3}
            >
              <Grid
                item
                xs={4}
                md={4}
              >
                <TextField
                  error={Boolean(touched.fname && errors.fname)}
                  fullWidth
                  helperText={touched.fname && errors.fname}
                  label="First name"
                  margin="normal"
                  name="fname"
                  size="small"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  type="text"
                  value={values.fname}
                  variant="outlined"
                />
              </Grid>
              <Grid
                item
                xs={4}
                md={4}
              >
                <TextField
                  fullWidth
                  label="Middle name"
                  margin="normal"
                  name="mname"
                  size="small"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  type="text"
                  value={values.mname}
                  variant="outlined"
                />
              </Grid>
              <Grid
                item
                xs={4}
                md={4}
              >
                <TextField
                  error={Boolean(touched.lname && errors.lname)}
                  fullWidth
                  helperText={touched.lname && errors.lname}
                  label="Last name"
                  margin="normal"
                  name="lname"
                  size="small"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  type="text"
                  value={values.lname}
                  variant="outlined"
                />
              </Grid>
            </Grid>
            <PhoneInput
              country="au"
              inputStyle={{
                width: '100%',
                borderRadius: '5px'
              }}
              dropdownStyle={{
                height: '160px'
              }}
              containerStyle={{ marginBottom: '10px', marginTop: '10px' }}
              specialLabel="Phone"
              countryCodeEditable={false}
              value={values.phoneNumber}
              onChange={(phone) => { values.phoneNumber = phone; }}
            />
            <TextField
              error={Boolean(touched.email && errors.email)}
              fullWidth
              helperText={touched.email && errors.email}
              label="Email Address"
              margin="normal"
              name="email"
              size="small"
              onBlur={handleBlur}
              onChange={(registerInfo && !registerInfo.asClientOrg) ? null : handleChange}
              type="email"
              value={values.email}
              variant="outlined"
              InputProps={{ readOnly: emailIdentified }}
            />
            <TextField
              error={Boolean(touched.password && errors.password)}
              fullWidth
              helperText={touched.password && errors.password}
              label="Password"
              margin="normal"
              size="small"
              name="password"
              onBlur={handleBlur}
              onChange={handleChange}
              type={showPassword ? 'text' : 'password'}
              value={values.password}
              variant="outlined"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => { setShowPassword(!showPassword); }}
                    >
                      {showPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            {!emailIdentified && (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Typography
                  sx={{ pr: 1 }}
                >
                  Send verification code to
                </Typography>
                <FormControlLabel
                  control={(
                    <Radio
                      checked={isToEmail}
                      onChange={() => setToEmail(true)}
                    />
                  )}
                  label="Email"
                  sx={{ pr: 1 }}
                />
                <FormControlLabel
                  control={(
                    <Radio
                      checked={!isToEmail}
                      onChange={() => setToEmail(false)}
                    />
                  )}
                  label="Phone number"
                />
                <PopupTooltip>
                  <Typography>
                    ACCZIOM will send a verification code to your email or phone number you used to create the account.
                    This is an added security step that ensures only you (or someone else who is authorized to access your account) gains entry.
                    You need to enter a verification code if you try to create a new account or reset your password.
                  </Typography>
                </PopupTooltip>
              </Box>
            )}
            {!isToEmail && (
              <Typography
                color="secondary.main"
              >
                Email is your account identifier and very important. Please input an email address that you use.
              </Typography>
            )}
            <ReCAPTCHA
              sitekey="6LdN6yscAAAAAPwtPgU6isHo02fytnOoaczxfQ-G"
              size="invisible"
              ref={captchaRef}
            />
            {/* {errors.submit && (
              <Box sx={{ mt: 3 }}>
                <FormHelperText error>
                  {errors.submit}
                </FormHelperText>
              </Box>
            )} */}
            <Box sx={{ mt: 2 }}>
              <Button
                color="primary"
                disabled={isSubmitting}
                fullWidth
                size="large"
                type="submit"
                variant="contained"
                sx={{
                  mt: 2,
                  mb: 2
                }}
              >
                {isSubmitting && (
                  <CircularProgress
                    color="primary"
                    size={20}
                    sx={{
                      marginRight: '8px'
                    }}
                  />
                )}
                Create My Account
              </Button>
              {registerInfo?.asClientOrg && (
                <Box
                  display="flex"
                  justifyContent="center"
                >
                  <Button
                    color="primary"
                    size="small"
                    variant="text"
                    onClick={onGotoLogin}
                  >
                    Already has an account?
                  </Button>
                </Box>
              )}
              {/* <Typography
                color="textSecondary"
                gutterBottom
                // align="center"
                variant="body2"
                mx={3}
                mt={2}
              >
                If you were invited by an Organization to Accziom, either use the URL in the invite email or search for the Organization
                &nbsp;
                <span>
                  <Link
                    component={RouterLink}
                    to="/marketplace/organization"
                  >
                    here
                  </Link>
                </span>
                .
                <br />
                Optimally, we can try to track all arrivals to their inviting source. In time we hope the Accziom brand can grow and attract people directly.
              </Typography> */}
            </Box>
          </form>
        )}
      </Formik>
    </>
  );
};

export default RegisterAmplify;
